import { Box } from '@mui/material';
import { DeletedMainActions } from './components/deleted-main-actions';
import { styles } from './styles';
import { DeletedFileGridView } from './components/deleted-file-grid-view';
import { DeletedActionsModals } from './components/deleted-actions-modals';
import { useMemo, useState } from 'react';
import { DeletedFileListView } from './components/deleted-file-list-view';
import {
  handleSetIsGridDefaultFolders,
  handleSetSearchValue,
} from 'redux-store/slices/file-cabinet-slice';
import { useAppDispatch } from 'redux-store/hooks/useAppDispatch';
import { useAppSelector } from 'redux-store/hooks/useAppSelector';
import { TrashInfoHeaderCard } from './components/trash-info-header-card';
import { IDeletedFilesProps } from 'components/file-cabinet-folders/modals/project-file-cabinet-modal/types';
import { useModalState } from 'hooks/useModalState';
import {
  TProjectDeletedFile,
  useDeleteProjectFileCabinetAllItemsPermanentlyMutation,
  useDeleteProjectFileCabinetItemPermanentlyMutation,
  useRestoreProjectFileCabinetItemMutation,
} from 'redux-store/api/file-cabinet';
import { EmptyTrashLabel } from './components/empty-trash-label';
import { useCatchServerError } from 'hooks/useCatchServerError';

interface IDeletedView {
  deletedFiles: TProjectDeletedFile[];
  isGrid: boolean;
  projectId: number;
  deletedFilesProps: IDeletedFilesProps;
}

export const DeletedView = ({
  deletedFiles,
  isGrid,
  projectId,
  deletedFilesProps,
}: IDeletedView) => {
  const dispatch = useAppDispatch();
  const { catchError } = useCatchServerError();

  const [fileToView, setFileToView] = useState<TProjectDeletedFile | null>(null);
  const [fileToDeleteId, setFileToDeletedId] = useState<number | null>(null);
  const [isDeleteAllFilesModalOpen, openDeleteAllFilesModal, closeDeleteAllFilesModal] =
    useModalState();

  const searchValue = useAppSelector((state) => state.fileCabinet.searchValue) || '';

  const [deleteProjectFileCabinetAllItemsPermanently, { isLoading: isDeletingAllItems }] =
    useDeleteProjectFileCabinetAllItemsPermanentlyMutation();

  const [restoreFileCabinetItem] = useRestoreProjectFileCabinetItemMutation();

  const [deleteProjectFileCabinetItemPermanently, { isLoading: isDeletingItem }] =
    useDeleteProjectFileCabinetItemPermanentlyMutation();

  const deletedFilesList = useMemo(() => {
    return deletedFiles?.filter((item) =>
      item?.name?.toLowerCase()?.includes(searchValue?.toLowerCase())
    );
  }, [searchValue, deletedFiles]);

  const onViewChange = (value: boolean) => {
    dispatch(handleSetIsGridDefaultFolders(value));
  };

  const onSearchChange = (value: string) => {
    dispatch(handleSetSearchValue(value || null));
  };

  const onViewFile = (file: TProjectDeletedFile) => {
    setFileToView(file);
  };

  const clearFileToDeletedId = () => {
    setFileToDeletedId(null);
  };

  const clearFileToView = () => {
    setFileToView(null);
  };

  const onDeleteFile = (fileId: number) => {
    setFileToDeletedId(fileId);
  };

  const onRestoreFile = (fileId: number) => {
    restoreFileCabinetItem({ itemId: fileId, projectId }).unwrap().catch(catchError);
  };

  const onConfirmDelete = () => {
    deleteProjectFileCabinetItemPermanently({ itemId: fileToDeleteId, projectId })
      .unwrap()
      .catch(catchError)
      .finally(clearFileToDeletedId);
  };

  const onConfirmDeleteAllItems = () => {
    deleteProjectFileCabinetAllItemsPermanently({ projectId })
      .unwrap()
      .catch(catchError)
      .finally(closeDeleteAllFilesModal);
  };

  const noItems = !deletedFilesList?.length;

  return (
    <Box sx={styles.container}>
      <DeletedMainActions
        noItems={noItems}
        deletedFilesProps={deletedFilesProps}
        searchValue={searchValue}
        isGrid={isGrid}
        onSearchChange={onSearchChange}
        onViewChange={onViewChange}
        openDeleteAllFilesModal={openDeleteAllFilesModal}
      />
      <TrashInfoHeaderCard />
      {noItems && <EmptyTrashLabel />}
      {isGrid ? (
        <DeletedFileGridView
          deletedFiles={deletedFilesList}
          onViewFile={onViewFile}
          onDeleteFile={onDeleteFile}
          onRestoreFile={onRestoreFile}
        />
      ) : (
        <DeletedFileListView
          deletedFiles={deletedFilesList}
          onViewFile={onViewFile}
          onDeleteFile={onDeleteFile}
          onRestoreFile={onRestoreFile}
        />
      )}
      <DeletedActionsModals
        isDeletingAllItems={isDeletingAllItems}
        isDeletingItem={isDeletingItem}
        isDeleteAllFilesModalOpen={isDeleteAllFilesModalOpen}
        projectId={projectId}
        fileToDeleteId={fileToDeleteId}
        fileToView={fileToView}
        clearFileToDeletedId={clearFileToDeletedId}
        clearFileToView={clearFileToView}
        closeDeleteAllFilesModal={closeDeleteAllFilesModal}
        onConfirmDelete={onConfirmDelete}
        onConfirmDeleteAllItems={onConfirmDeleteAllItems}
      />
    </Box>
  );
};
